import { Tooltip } from '@mui/material';
import {
  JSX,
  ReactNode,
  RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

// Matches MUI Tooltip's defaults so delegated and wrapped tooltips feel the same
const ENTER_DELAY_MS = 100;
const ENTER_TOUCH_DELAY_MS = 700;
const LEAVE_TOUCH_DELAY_MS = 1500;
// iOS Safari only honors the prefixed property
const USER_SELECT_PROPERTY = '-webkit-user-select';

interface DelegatedTooltipProps {
  containerRef?: RefObject<HTMLElement | null>;
  selector?: string;
  getContent?: (element: HTMLElement) => ReactNode;
}

interface TooltipState {
  anchor: HTMLElement;
  content: ReactNode;
  open: boolean;
}

function getDataAttributeContent(element: HTMLElement): ReactNode {
  const html = element.dataset.tooltipHtml;
  if (html) {
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  }

  return element.dataset.tooltip ?? null;
}

export default function DelegatedTooltip({
  containerRef,
  selector = '[data-tooltip], [data-tooltip-html]',
  getContent = getDataAttributeContent,
}: DelegatedTooltipProps): JSX.Element {
  const id = useId();
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const getContentRef = useRef(getContent);

  useEffect(() => {
    getContentRef.current = getContent;
  }, [getContent]);

  useEffect(() => {
    const root = containerRef ? containerRef.current : document;
    if (!root) {
      return;
    }

    const current: {
      anchor: HTMLElement | null;
      isOpen: boolean;
      pressPointerId: number | null;
      lastInputWasTouch: boolean;
      timer?: number;
      leaveTimer?: number;
      previousUserSelect?: string;
    } = {
      anchor: null,
      isOpen: false,
      pressPointerId: null,
      lastInputWasTouch: false,
    };

    const findAnchor = (target: EventTarget | null): HTMLElement | null =>
      target instanceof Element && root.contains(target)
        ? target.closest<HTMLElement>(selector)
        : null;

    const restoreUserSelect = () => {
      if (current.previousUserSelect === undefined) {
        return;
      }

      document.body.style.setProperty(
        USER_SELECT_PROPERTY,
        current.previousUserSelect
      );
      current.previousUserSelect = undefined;
    };

    const close = () => {
      if (!current.isOpen) {
        return;
      }

      current.isOpen = false;
      setTooltip((previous) =>
        previous ? { ...previous, open: false } : null
      );
    };

    const show = (anchor: HTMLElement, delay: number) => {
      window.clearTimeout(current.timer);
      window.clearTimeout(current.leaveTimer);
      current.anchor = anchor;
      current.timer = window.setTimeout(() => {
        restoreUserSelect();
        if (!anchor.isConnected) {
          hide();
          return;
        }

        const content = getContentRef.current(anchor);
        if (content === null || content === '') {
          close();
          return;
        }

        current.isOpen = true;
        setTooltip({ anchor, content, open: true });
      }, delay);
    };

    const hide = () => {
      window.clearTimeout(current.timer);
      window.clearTimeout(current.leaveTimer);
      restoreUserSelect();
      current.pressPointerId = null;
      current.anchor = null;
      close();
    };

    const handleEnter = (event: Event) => {
      if (event instanceof PointerEvent && event.pointerType === 'touch') {
        return;
      }

      const anchor = findAnchor(event.target);
      if (anchor === current.anchor) {
        return;
      }

      if (!anchor) {
        hide();
        return;
      }

      if (event.type === 'focusin') {
        !current.lastInputWasTouch &&
          anchor.matches(':focus-visible') &&
          show(anchor, 0);
        return;
      }

      show(anchor, current.isOpen ? 0 : ENTER_DELAY_MS);
    };

    const handleLeave = (event: Event) => {
      if (event instanceof PointerEvent && event.pointerType === 'touch') {
        return;
      }

      const nextTarget =
        event instanceof MouseEvent || event instanceof FocusEvent
          ? event.relatedTarget
          : null;
      if (findAnchor(nextTarget)) {
        return;
      }

      hide();
    };

    const handlePointerDown = (event: Event) => {
      if (!(event instanceof PointerEvent)) {
        return;
      }

      current.lastInputWasTouch = event.pointerType === 'touch';
      hide();

      if (!current.lastInputWasTouch || !event.isPrimary) {
        return;
      }

      const anchor = findAnchor(event.target);
      if (!anchor) {
        return;
      }

      current.pressPointerId = event.pointerId;
      current.previousUserSelect =
        document.body.style.getPropertyValue(USER_SELECT_PROPERTY);
      document.body.style.setProperty(USER_SELECT_PROPERTY, 'none');
      show(anchor, ENTER_TOUCH_DELAY_MS);
    };

    const handlePointerUp = (event: Event) => {
      if (
        !(event instanceof PointerEvent) ||
        event.pointerId !== current.pressPointerId
      ) {
        return;
      }

      current.pressPointerId = null;
      window.clearTimeout(current.timer);
      restoreUserSelect();
      current.leaveTimer = window.setTimeout(hide, LEAVE_TOUCH_DELAY_MS);
    };

    const handleKeyDown = () => {
      current.lastInputWasTouch = false;
    };

    root.addEventListener('pointerover', handleEnter);
    root.addEventListener('pointerout', handleLeave);
    root.addEventListener('focusin', handleEnter);
    root.addEventListener('focusout', handleLeave);
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('pointercancel', handlePointerUp, true);
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('scroll', hide, true);

    return () => {
      hide();
      root.removeEventListener('pointerover', handleEnter);
      root.removeEventListener('pointerout', handleLeave);
      root.removeEventListener('focusin', handleEnter);
      root.removeEventListener('focusout', handleLeave);
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerUp, true);
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, [containerRef, selector]);

  useEffect(() => {
    const anchor = tooltip?.open ? tooltip.anchor : null;
    if (!anchor) {
      return;
    }

    anchor.setAttribute('aria-describedby', id);
    return () => {
      anchor.removeAttribute('aria-describedby');
    };
  }, [tooltip, id]);

  return (
    <Tooltip
      id={id}
      open={tooltip?.open ?? false}
      title={tooltip?.content ?? ''}
      slotProps={{ popper: { anchorEl: tooltip?.anchor } }}
    >
      <span hidden />
    </Tooltip>
  );
}
